import { type FC, useEffect } from 'react';
import { ArrowPathIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useStockSyncRuns } from '../../hooks/useSyncRuns';
import { latestRunByStore } from '../../services/syncService';
import {
    ErrorBanner,
    Notable,
    NoticeBanner,
    Panel,
    RunButton,
    StatusBadge,
    Table,
    TriggerBadge,
} from './syncUi';
import { formatDateTime, formatNumber, formatRelative } from './syncFormat';

/**
 * Stock sync: Arch's stock figures, pulled per store every fifteen minutes.
 *
 * Read-only for now. The counters are shown next to the status deliberately — a run
 * can report SUCCEEDED having received twenty thousand products and written none of
 * them, which looks identical to a healthy run if you only read the status.
 */
const StockSyncPage: FC = () => {
    const {
        runs, stores, loading, error, notice, triggeringStoreId,
        fetchRuns, runSync, dismissNotice,
    } = useStockSyncRuns();

    useEffect(() => {
        void fetchRuns();
    }, [fetchRuns]);

    const latest = latestRunByStore(runs);
    const unmapped = stores.filter((s) => s.external_store_id === null);

    return (
        <div className="space-y-6">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-semibold text-stone-900">Stock Sync</h2>
                    <p className="mt-1 text-sm text-stone-500">
                        Stock levels pulled from Arch, one store at a time, every fifteen minutes.
                        Each run records what Arch sent and what we were able to apply.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={() => void fetchRuns()}
                    disabled={loading}
                    className="inline-flex items-center gap-2 rounded-xl border border-stone-200 bg-white px-4 py-2.5 text-sm font-medium text-stone-700 shadow-sm hover:bg-stone-50 transition-colors disabled:cursor-not-allowed disabled:opacity-60"
                >
                    <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                    Refresh
                </button>
            </div>

            {error && <ErrorBanner message={error} />}
            {notice && <NoticeBanner message={notice} onDismiss={dismissNotice} />}

            {unmapped.length > 0 && (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 flex items-start gap-3">
                    <ExclamationTriangleIcon className="h-5 w-5 text-amber-700 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-amber-800">
                        <span className="font-semibold">
                            {unmapped.map((s) => s.name).join(', ')}
                        </span>{' '}
                        {unmapped.length === 1 ? 'has' : 'have'} no Arch store id, so{' '}
                        {unmapped.length === 1 ? 'it is' : 'they are'} skipped by every sync. Mapping a
                        store to Arch is done by hand.
                    </p>
                </div>
            )}

            <Panel
                title="Freshness by store"
                description="The most recent run for each store. This is how current our figures are."
            >
                <Table headers={['Store', 'Last run', 'Status', 'Received', 'Written', 'Unmatched', 'Rejected', '']}>
                    {loading && stores.length === 0 ? (
                        <tr><td colSpan={8} className="py-10 px-6 text-stone-400">Loading…</td></tr>
                    ) : stores.length === 0 ? (
                        <tr><td colSpan={8} className="py-10 px-6 text-stone-400">No stores found</td></tr>
                    ) : (
                        stores.map((store) => {
                            const run = latest.get(store.id);
                            return (
                                <tr key={store.id} className="hover:bg-stone-50/50 transition-colors">
                                    <td className="py-4 px-6 font-medium text-stone-900">
                                        {store.name}
                                        {store.external_store_id === null && (
                                            <span className="ml-2 text-xs text-amber-700">not mapped</span>
                                        )}
                                    </td>
                                    <td className="py-4 px-6 text-stone-600">
                                        {run ? formatRelative(run.created_at) : 'never'}
                                    </td>
                                    <td className="py-4 px-6">
                                        {run ? <StatusBadge status={run.status} /> : <span className="text-stone-400">—</span>}
                                    </td>
                                    <td className="py-4 px-6 text-stone-600">{formatNumber(run?.products_received)}</td>
                                    <td className="py-4 px-6 text-stone-900 font-medium">{formatNumber(run?.stock_updated)}</td>
                                    <td className="py-4 px-6">{run ? <Notable value={run.unmatched} /> : '—'}</td>
                                    <td className="py-4 px-6">{run ? <Notable value={run.rejected} /> : '—'}</td>
                                    <td className="py-4 px-6 text-right">
                                        <RunButton
                                            onClick={() => void runSync(store.id)}
                                            busy={triggeringStoreId === store.id}
                                            // Deliberately NOT disabled while a run is IN_PROGRESS.
                                            // A run parked by an unreachable Arch is exactly when
                                            // you want this: the claim adopts the stale run and
                                            // carries on. If one really is live the function answers
                                            // BUSY, which the notice explains. Only an unmapped
                                            // store — which has nothing to sync — is refused here.
                                            disabled={
                                                store.external_store_id === null ||
                                                triggeringStoreId !== null
                                            }
                                            label={run?.status === 'IN_PROGRESS' ? 'Resume' : 'Run now'}
                                            title={
                                                store.external_store_id === null
                                                    ? 'This store has no Arch store id, so there is nothing to sync'
                                                    : run?.status === 'IN_PROGRESS'
                                                        ? 'Continue the run that is already open for this store'
                                                        : undefined
                                            }
                                        />
                                    </td>
                                </tr>
                            );
                        })
                    )}
                </Table>
            </Panel>

            <Panel
                title="Recent runs"
                description="Every run, newest first. Received should equal written plus unmatched plus rejected."
            >
                <Table
                    headers={['Started', 'Store', 'Trigger', 'Status', 'Window', 'Received', 'Written', 'Unmatched', 'Rejected', 'Off sale', 'Back on', 'Prices', 'Error']}
                >
                    {loading && runs.length === 0 ? (
                        <tr><td colSpan={13} className="py-10 px-6 text-stone-400">Loading…</td></tr>
                    ) : runs.length === 0 ? (
                        <tr>
                            <td colSpan={13} className="py-10 px-6 text-stone-400">
                                No runs yet. The scheduled job runs every fifteen minutes.
                            </td>
                        </tr>
                    ) : (
                        runs.map((run) => {
                            const store = stores.find((s) => s.id === run.store_id);
                            return (
                                <tr key={run.id} className="hover:bg-stone-50/50 transition-colors">
                                    <td className="py-4 px-6 text-stone-600 whitespace-nowrap">
                                        {formatDateTime(run.started_at ?? run.created_at)}
                                    </td>
                                    <td className="py-4 px-6 text-stone-900">{store?.name ?? '—'}</td>
                                    <td className="py-4 px-6"><TriggerBadge trigger={run.trigger_type} /></td>
                                    <td className="py-4 px-6"><StatusBadge status={run.status} /></td>
                                    <td className="py-4 px-6 text-stone-500 whitespace-nowrap">{run.last_updated_param}</td>
                                    <td className="py-4 px-6 text-stone-600">{formatNumber(run.products_received)}</td>
                                    <td className="py-4 px-6 text-stone-900 font-medium">{formatNumber(run.stock_updated)}</td>
                                    <td className="py-4 px-6"><Notable value={run.unmatched} /></td>
                                    <td className="py-4 px-6"><Notable value={run.rejected} /></td>
                                    <td className="py-4 px-6"><Notable value={run.deactivated} /></td>
                                    <td className="py-4 px-6"><Notable value={run.reactivated} tone="info" /></td>
                                    <td className="py-4 px-6">
                                        <Notable value={run.prices_updated} tone="info" />
                                        {run.prices_skipped > 0 && (
                                            <span
                                                className="ml-2 text-xs text-amber-700"
                                                title="Rows carrying a price we would not write — missing, zero or negative. The existing price was left alone."
                                            >
                                                {run.prices_skipped} skipped
                                            </span>
                                        )}
                                    </td>
                                    <td className="py-4 px-6 text-rose-700 max-w-xs truncate" title={run.last_error ?? ''}>
                                        {run.last_error ?? ''}
                                    </td>
                                </tr>
                            );
                        })
                    )}
                </Table>
            </Panel>
        </div>
    );
};

export default StockSyncPage;
